/**
 * Full-screen wellness check-in confirm — presented when the recipient TAPS a
 * wellness check-in push (the lock-screen "I'm OK" action stays silent; this
 * is the in-app equivalent with an actual moment of feedback).
 *
 * One giant circle: red "I'm OK" → tap → springs to green with a check while
 * the ack posts (/wellness/im-ok — same endpoint the notification action and
 * the watch use; it cancels any pending escalation ladder server-side), a
 * success haptic fires, and the sheet dismisses itself. Failure rolls back to
 * red with a retry note — the recipient must never believe they checked in
 * when the server didn't hear it.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Modal, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { apiWellnessPost } from '../utilities/apiClient';
import { hapticCreationPulse } from '../utilities/haptics';
import Icon from './Icon';
import debug from '../utilities/debug';
import {
  PendingWellnessCheckIn,
  subscribeWellnessCheckIn,
  clearWellnessCheckIn,
} from '../utilities/wellnessCheckInStore';
import {
  QUICK_ACTIONS,
  buildSymptomPayload,
  type QuickActionKey,
} from '../utilities/checkInQuickActions';

const RED = '#EF4444';
const GREEN = '#10B981';
const DISMISS_AFTER_MS = 1400;

export default function WellnessCheckInSheet(): React.JSX.Element | null {
  const theme = useTheme();
  const [pending, setPending] = useState<PendingWellnessCheckIn | null>(null);
  const [phase, setPhase] = useState<'idle' | 'confirming' | 'done' | 'error'>('idle');
  // "I'm OK, but…". Flags only — the circle is still the only thing that sends,
  // so the one-tap path for someone who is simply fine is untouched.
  const [selected, setSelected] = useState<Set<QuickActionKey>>(new Set());

  // 0 → red idle, 1 → green confirmed. Colors interpolate on the JS driver;
  // the tick gets its own native-driver spring.
  const progress = useRef(new Animated.Value(0)).current;
  const tickScale = useRef(new Animated.Value(0)).current;
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // subscribe replays any already-pending check-in (push tapped pre-login)
    // and returns the unsubscribe, which this effect explicitly hands back so
    // an unmounted sheet can never receive a late setState.
    const unsubscribe = subscribeWellnessCheckIn((p) => {
      setPending(p);
      if (p) {
        setPhase('idle');
        setSelected(new Set());
        progress.setValue(0);
        tickScale.setValue(0);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
  }, []);

  const confirm = useCallback(async () => {
    if (!pending || phase === 'confirming' || phase === 'done') return;
    setPhase('confirming');

    try {
      const ack = await apiWellnessPost(`/im-ok/${pending.projectId}/${pending.accountId}/${pending.cognitoId}`, {});

      // apiWellnessPost RESOLVES a 4xx/5xx into { success: false, ... } rather
      // than throwing (apiClient failure()), so the catch below only ever saw
      // transport errors. A rejected ack therefore drew the green tick and
      // dismissed the sheet while the escalation ladder kept running — the
      // exact lie the rollback in that catch exists to prevent. Throwing here
      // routes a rejected ack into that rollback, which is already correct.
      //
      // `=== false` and not `!ack`: only an explicit failure counts, so a 200
      // whose body omits the flag is still treated as success.
      if (ack?.success === false) {
        throw new Error(ack?.message || 'Acknowledgement was not recorded.');
      }

      // Symptoms go SECOND, and their failure is swallowed.
      //
      // The ack is the safety-critical half: it cancels the escalation ladder,
      // and this file exists because "the recipient must never believe they
      // checked in when the server didn't hear it". Rolling the circle back to
      // red because a note failed to attach would recreate exactly that lie in
      // the other direction — she checked in, the ladder is cancelled, and the
      // screen tells her it didn't work.
      //
      // Separate endpoint rather than a field on im-ok: manual-entry already
      // validates these types, records `entered_by` provenance, and files a
      // checkin_answered event. It also accepts a note with no readings, which
      // is exactly the shape a set of one-tap flags produces.
      const symptoms = buildSymptomPayload(Array.from(selected));
      if (symptoms) {
        apiWellnessPost(
          `/manual-entry/${pending.projectId}/${pending.accountId}/${pending.cognitoId}`,
          symptoms,
        ).catch((err: any) => debug.warn('[wellness-checkin] symptom note failed', { err: err?.message }));
      }

      Animated.timing(progress, {
        toValue: 1,
        duration: 420,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();
      Animated.spring(tickScale, {
        toValue: 1,
        friction: 4,
        tension: 90,
        delay: 180,
        useNativeDriver: true,
      }).start();

      hapticCreationPulse();
      setPhase('done');
      dismissTimer.current = setTimeout(() => clearWellnessCheckIn(), DISMISS_AFTER_MS);
    } catch (err: any) {
      debug.warn('[wellness-checkin] im-ok failed', { err: err?.message });
      // Roll back — a failed ack must not look like a successful one.
      Animated.timing(progress, { toValue: 0, duration: 220, useNativeDriver: false }).start();
      Animated.timing(tickScale, { toValue: 0, duration: 150, useNativeDriver: true }).start();
      setPhase('error');
    }
  }, [pending, phase, progress, tickScale, selected]);

  const toggleAction = useCallback((key: QuickActionKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const dismiss = useCallback(() => {
    if (phase === 'confirming') return;
    clearWellnessCheckIn();
  }, [phase]);

  if (!pending) return null;

  const circleColor = progress.interpolate({ inputRange: [0, 1], outputRange: [RED, GREEN] });
  const haloColor = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(239,68,68,0.18)', 'rgba(16,185,129,0.18)'],
  });

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={dismiss}>
      <View style={[styles.overlay, { backgroundColor: theme.dark ? 'rgba(6,10,18,0.96)' : 'rgba(255,255,255,0.97)' }]}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Are you OK?</Text>
        <Text style={[styles.subtitle, { color: theme.colors.muted }]}>
          Your care team asked to check in. One tap lets them know you’re alright.
        </Text>

        <TouchableOpacity activeOpacity={0.85} onPress={confirm} disabled={phase === 'done'}>
          <Animated.View style={[styles.halo, { backgroundColor: haloColor }]}>
            <Animated.View style={[styles.circle, { backgroundColor: circleColor }]}>
              {phase === 'done' || phase === 'confirming' ? (
                <Animated.View style={{ transform: [{ scale: tickScale }] }}>
                  <Icon icon={['fas', 'check']} size={64} color="#fff" />
                </Animated.View>
              ) : (
                <Text style={styles.circleText}>I’m OK</Text>
              )}
            </Animated.View>
          </Animated.View>
        </TouchableOpacity>

        {/* Anything worth mentioning, without turning this into a form.
            Hidden the moment the tap lands: the green tick is the one clear
            "done" signal on this screen and nothing should compete with it.

            These are optional and clearly secondary — someone who is simply
            fine taps the circle and never reads this row. */}
        {phase !== 'done' && phase !== 'confirming' && (
          <View style={styles.actionsWrap}>
            <Text style={[styles.actionsHint, { color: theme.colors.muted }]}>
              Anything you want them to know?
            </Text>
            <View style={styles.actionsRow}>
              {QUICK_ACTIONS.map((action) => {
                const on = selected.has(action.key);
                return (
                  <TouchableOpacity
                    key={action.key}
                    style={[
                      styles.actionChip,
                      {
                        borderColor: on ? RED : theme.colors.border,
                        backgroundColor: on ? 'rgba(239,68,68,0.10)' : 'transparent',
                      },
                    ]}
                    onPress={() => toggleAction(action.key)}
                    activeOpacity={0.75}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: on }}
                    accessibilityLabel={action.label}
                  >
                    <Icon
                      icon={action.icon as any}
                      size={14}
                      color={on ? RED : theme.colors.muted}
                    />
                    <Text style={[styles.actionChipText, { color: on ? RED : theme.colors.text }]}>
                      {action.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            {selected.size > 0 && (
              // Says what the circle will now do. Selecting a symptom and
              // walking away must not leave someone believing they reported it.
              <Text style={[styles.actionsHint, { color: theme.colors.muted, marginTop: 10 }]}>
                Tap {'I’m OK'} to send this with your check-in.
              </Text>
            )}
          </View>
        )}

        <Text style={[styles.status, { color: phase === 'error' ? RED : theme.colors.muted }]}>
          {phase === 'done'
            ? 'Thanks — your care team has been told.'
            : phase === 'error'
              ? 'That didn’t go through — tap to try again.'
              : ' '}
        </Text>

        {phase !== 'done' && phase !== 'confirming' && (
          <TouchableOpacity onPress={dismiss} hitSlop={{ top: 10, bottom: 10, left: 20, right: 20 }}>
            <Text style={[styles.notNow, { color: theme.colors.muted }]}>Not now</Text>
          </TouchableOpacity>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32 },
  title: { fontSize: 30, fontWeight: '800', marginBottom: 10, textAlign: 'center' },
  subtitle: { fontSize: 15, lineHeight: 21, textAlign: 'center', marginBottom: 40, maxWidth: 320 },
  halo: {
    width: 216, height: 216, borderRadius: 108,
    alignItems: 'center', justifyContent: 'center',
  },
  circle: {
    width: 176, height: 176, borderRadius: 88,
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.18, shadowRadius: 16,
    elevation: 8,
  },
  circleText: { color: '#fff', fontSize: 26, fontWeight: '800' },
  actionsWrap: { marginTop: 30, alignItems: 'center', maxWidth: 340 },
  actionsHint: { fontSize: 13, textAlign: 'center', marginBottom: 12 },
  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8 },
  actionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    // Generous target: the person tapping this may be unsteady, which is
    // frequently the reason they are tapping it.
    paddingHorizontal: 14,
    paddingVertical: 11,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  actionChipText: { fontSize: 15, fontWeight: '600' },
  status: { fontSize: 14, marginTop: 28, textAlign: 'center', minHeight: 20 },
  notNow: { fontSize: 15, fontWeight: '600', marginTop: 18, padding: 6 },
});
